export type Outcome = 'good' | 'not_great' | 'need_help' | 'no_answer';
export type FollowUp = 'none' | 'care_team_review' | 'urgent';

export type InteractionType = 'conversation' | 'check_in' | 'scrapbook';

// Keep in sync with infra/lambdas/shared/interaction.ts.
export const INTERACTION_TYPES: InteractionType[] = ['conversation', 'check_in', 'scrapbook'];

export const INTERACTION_TYPE_LABELS: Record<InteractionType, string> = {
  conversation: 'Conversation',
  check_in: 'Check-in',
  scrapbook: 'Scrapbook',
};

export const INTERACTION_TYPE_DESCRIPTIONS: Record<InteractionType, string> = {
  conversation: 'A warm, open-ended chat — good for connection and company.',
  check_in: "A brief safety check — confirms they're okay and don't need help, then ends.",
  scrapbook:
    'Recurring reminiscence calls that gently build a scrapbook of the member’s life stories for the family, while exercising memory.',
};

// Keep in sync with infra/lambdas/shared/identity.ts (and the /identity-mode endpoint).
export type IdentityMode = 'self' | 'proxy';
export const IDENTITY_MODES: IdentityMode[] = ['self', 'proxy'];

export const IDENTITY_MODE_LABELS: Record<IdentityMode, string> = {
  self: 'As a Hey Be Sure companion',
  proxy: "On the family's behalf",
};

export const IDENTITY_MODE_DESCRIPTIONS: Record<IdentityMode, string> = {
  self: 'The companion introduces itself by name (e.g. "This is Daniel from Hey Be Sure").',
  proxy: 'The companion says it is calling on the family’s behalf (e.g. "Hey Be Sure — your family asked me to check in").',
};

export interface NotificationPrefs {
  phoneE164: string | null;
  notifyEmail: boolean;
  notifySms: boolean;
  notifyDailySummary: boolean;
  notifyUrgentAlerts: boolean;
}

export type AccountType = 'individual' | 'community';
export type CommunityRole = 'admin' | 'staff';

export interface SubscriberMe extends NotificationPrefs {
  subscriberId: string;
  email: string;
  displayName: string;
  accountType: AccountType;
  role?: CommunityRole; // present when accountType === 'community'
  communityName?: string | null;
}

export interface CheckInStructured {
  concerns?: string[];
  recommendedFollowUp?: FollowUp;
  terminationReason?: string;
  validationError?: string;
}

export interface ClientLastCheckIn {
  checkInId: string;
  outcome: Outcome | null;
  summary: string | null;
  completedAt: string | null;
}

export interface Client {
  memberId: string;
  displayName: string;
  phoneE164: string;
  interactionType?: InteractionType;
  lastCheckIn: ClientLastCheckIn | null;
}

export interface ClientCheckIn {
  checkInId: string;
  outcome: Outcome | null;
  summary: string | null;
  structured: CheckInStructured | null;
  startedAt: string;
  completedAt: string | null;
}

export interface ClientCheckInHistory {
  memberId: string;
  displayName: string;
  interactionType: InteractionType;
  identityMode: IdentityMode;
  checkIns: ClientCheckIn[];
}

export interface TriggerCallResult {
  ok: true;
  checkInId: string;
  callSid: string;
}

export interface SetInteractionTypeResult {
  ok: true;
  memberId: string;
  interactionType: InteractionType;
}

export interface SetIdentityModeResult {
  ok: boolean;
  memberId: string;
  identityMode: IdentityMode;
}

export interface DeleteMemoryResult {
  ok: true;
  deleted: number;
}

// ---- Call schedules -------------------------------------------------------
// daysOfWeek: 0=Sun .. 6=Sat. Window times are "HH:MM" in the member's timezone;
// the call fires at a random time inside [windowStart, windowEnd].
export interface Schedule {
  scheduleId: string;
  memberId: string;
  daysOfWeek: number[];
  windowStart: string; // "HH:MM"
  windowEnd: string; // "HH:MM"
  active: boolean;
  source: string;
  label: string | null;
}

export interface ScheduleInput {
  daysOfWeek: number[];
  windowStart: string;
  windowEnd: string;
  label?: string | null;
}

export interface SchedulesResponse {
  timezone: string;
  schedules: Schedule[];
}

export const DAY_LABELS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

/** Curated IANA timezones for the picker (label → value). */
export const TIMEZONE_OPTIONS: { value: string; label: string }[] = [
  { value: 'America/New_York', label: 'Eastern (New York)' },
  { value: 'America/Chicago', label: 'Central (Chicago)' },
  { value: 'America/Denver', label: 'Mountain (Denver)' },
  { value: 'America/Phoenix', label: 'Arizona (no DST)' },
  { value: 'America/Los_Angeles', label: 'Pacific (Los Angeles)' },
  { value: 'America/Anchorage', label: 'Alaska (Anchorage)' },
  { value: 'Pacific/Honolulu', label: 'Hawaii (Honolulu)' },
];
